/**
 * @typedef {[number, number]} Coordinate
 */

/**
 * @typedef {Coordinate[]} Coordinates
 */

/**
 * @typedef {Object} GeohashItem
 * @property {Coordinate} l - Initial coordinates for the geohash item, as a [lat, lng] pair.
 * @property {Coordinate[]} [b] - Optional breakpoints for connections, as an array of [lat, lng] pairs.
 * @property {Coordinate} [l2] - Optional final coordinates for connections, as a [lat, lng] pair.
 * @property {string} [c] - Optional color CSS string.
 * @property {number} [w] - Optional line width.
 * @property {number} [o] - Optional opacity (0–1).
 * @property {string} [p] - Optional line pattern (e.g., 'dashed').
 * @property {number} [z] - Optional z-index for rendering order.
 */

/**
 * @typedef {string} GeohashKey
 * A string that encodes the type of map entity (node, connection, or section) and its unique identifier.
 */

/**
 * @typedef {Record<GeohashKey, GeohashItem>} Geohash
 */

/**
 * Extracts coordinates from the provided geohash item, including initial coordinates, breakpoints, and final coordinates if present.
 * @param {GeohashItem} geohashItem
 * @returns {Coordinates} An array of coordinates.
 */
export function getCoordinatesFromGeohashItem(geohashItem) {
  // Start with the initial coordinates.
  // Note: Points will only have the initial coordinates.
  const initialCoordinates = validateCoordinates(geohashItem.l);
  const coordinates = [initialCoordinates];

  // Add breakpoints if present.
  const breakpoints = geohashItem.b;
  if (Array.isArray(breakpoints)) {
    breakpoints.forEach((breakpoint) => {
      const validatedBreakpoint = validateCoordinates(breakpoint);
      coordinates.push(validatedBreakpoint);
    });
  }

  // Add final coordinates if present.
  if (geohashItem.l2) {
    const finalCoordinates = validateCoordinates(geohashItem.l2);
    coordinates.push(finalCoordinates);
  }

  return coordinates;
}

/** Validates that a coordinate is a [number, number] pair.
 * @param {any} location
 * @returns {Coordinate} The validated coordinate.
 */
function validateCoordinates(location) {
  if (!Array.isArray(location) || location.length < 2 || typeof location[0] !== 'number' || typeof location[1] !== 'number') {
    throw new Error(`Invalid coordinate: expected [number, number], got ${JSON.stringify(location)}`);
  }
  return [location[0], location[1]];
}
